// Step — Welcome to the Team form (native; ports Jotform 243113890723050
// "Welcome to The Laughton Team at EveryPlace!", fetched 2026-07-20).
// Submits to the authenticated /v1/onboarding-gate/welcome-form endpoint;
// together with the signed ICA this opens the server-side gate that unlocks
// the transfer steps (ADRE onward). Payload is stored whole (JSONB) — field
// changes here need no backend change.
//
// The two "Getting to know you" dropdowns share one option list, recovered from
// all 202 live submissions (2026-07-20): Hometown? / Professional Background? /
// Education?. (An option never chosen wouldn't appear this way — verify against
// the Jotform editor if an extra choice is suspected.)

import React from "react";
import { IcArrow, IcCheck } from "./shared.jsx";
import { FooterNav, StepHeader } from "./steps_early.jsx";
import { api } from "../lib/api.js";

const { useState: wS, useEffect: wE } = React;

const F = { display: "block", width: "100%", padding: "12px 14px", marginBottom: 16,
  background: "var(--ep-bg-elevated)", border: "1px solid var(--ep-border)",
  color: "var(--ep-fg)", font: "400 15px/1.4 var(--ep-font-body)", boxSizing: "border-box" };
const ROW = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 };
// City / State / ZIP. City takes the slack because it is the only free-text one.
const ROW3 = { display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 12 };
const SEC = { margin: "28px 0 14px" };

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const GTKY_OPTIONS = ["Hometown?", "Professional Background?", "Education?"];
const STATES = ["AZ","AL","AK","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];

function slugName(s) { return (s || "").trim().toLowerCase().replace(/[^a-z]/g, ""); }

function StepWelcomeForm({ agent, onNext, onBack, gate, refreshGate }) {
  const parts = (agent?.name || "").split(" ");
  const [form, setForm] = wS({
    firstName: parts[0] || "", middleName: "", lastName: parts.slice(1).join(" ") || "",
    nameInAdre: agent?.name || "",
    // Four fields, not one. This used to be a single free-text line, which meant
    // the city and state could not be read back out of it without guessing —
    // Brokermint's City and State fields were left empty for exactly that reason,
    // and the zip was scraped off the end with a regex. `state` is the home
    // address state and is unrelated to `licenseState` below.
    address: "", city: "", state: "", zip: "",
    phone: agent?.phone || "",
    email: agent?.email || "",
    birthMonth: "", birthDay: "",
    licenseNumber: agent?.license || "",
    hasMlsId: "",            // "yes" | "no"
    mlsId: "",
    licenseState: "AZ",
    licenseDateIssued: "",
    referredBy: "",
    otherLanguages: "",
    instagramHandle: "",
    gtky1Question: "", gtky1Answer: "",
    gtky2Question: "", gtky2Answer: "",
    bringingPendingDeals: "", // "yes" | "no"
  });
  const [state, setState] = wS(gate?.welcomeDone ? "done" : "idle"); // idle | saving | done | error
  const [error, setError] = wS(null);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const emailPreview = form.firstName && form.lastName
    ? `${slugName(form.firstName)}.${slugName(form.lastName)}@laughtonteam.com` : null;

  // Required as of 2026-08-27: date issued, the referral source, and BOTH
  // getting-to-know-you pairs. They were optional, and Marina Ffriend submitted
  // with all four blank — which is legitimate under the old rule and still left
  // the team unable to write her public welcome post, since that is composed
  // entirely from the GTKY answers.
  //
  // Both halves of each pair are checked. A question with no answer is useless,
  // and an answer with no question is unlabelled prose that nobody can place.
  //
  // The address is required as of 2026-09-03, all four parts of it. Requiring the
  // city, state and zip but not the street would collect "Phoenix, AZ 85018" and
  // call it an address — it goes on a licence record and a CRM contact, where a
  // half address is no more useful than none.
  const zipOk = /^\d{5}(-\d{4})?$/.test(form.zip.trim());
  const missing = [
    !form.firstName            && "First name",
    !form.lastName             && "Last name",
    !form.address              && "Street address",
    !form.city                 && "City",
    !form.state                && "State",
    !form.zip                  && "ZIP code",
    (form.zip && !zipOk)       && "ZIP code (5 digits, or ZIP+4)",
    !form.phone                && "Phone number",
    !form.email                && "Email",
    !form.birthMonth           && "Birth month",
    !form.birthDay             && "Birth day",
    !form.licenseDateIssued    && "License date issued",
    !form.hasMlsId             && "MLS ID yes/no",
    (form.hasMlsId === "yes" && !form.mlsId) && "MLS ID",
    !form.referredBy           && "How you heard about us",
    !form.gtky1Question        && "Getting to know you — question #1",
    !form.gtky1Answer          && "Getting to know you — answer #1",
    !form.gtky2Question        && "Getting to know you — question #2",
    !form.gtky2Answer          && "Getting to know you — answer #2",
    !form.bringingPendingDeals && "Pending deals yes/no",
  ].filter(Boolean);
  const canSubmit = missing.length === 0;

  async function submit() {
    setState("saving"); setError(null);
    try {
      await api.post("/v1/onboarding-gate/welcome-form", form);
      await refreshGate?.();
      setState("done");
    } catch (err) {
      setError(err?.message || "Could not save — try again.");
      setState("error");
    }
  }

  const done = state === "done" || gate?.welcomeDone;

  // While awaiting the ICA, poll the gate every 10s so the footer unlocks on
  // its own the moment the BoldSign completion webhook lands.
  wE(() => {
    if (!done || gate?.unlocked) return;
    const t = setInterval(() => refreshGate?.(), 10_000);
    return () => clearInterval(t);
  }, [done, gate?.unlocked]); // eslint-disable-line react-hooks/exhaustive-deps

  const Cap = ({ children }) => <label className="ep-caption" style={{ display: "block", marginBottom: 6 }}>{children}</label>;
  const Sec = ({ children }) => <div className="ep-eyebrow" style={{ ...SEC, color: "var(--ep-sunset)" }}>{children}</div>;

  return (
    <div style={{ maxWidth: 720 }}>
      <StepHeader
        eyebrow="Welcome to The Laughton Team"
        title="Tell us about you."
        subtitle="This sets up your team email, profile, and marketing intro. Your transfer steps unlock once this and your ICA are complete."/>

      {done ? (
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "20px 22px",
          background: "var(--ep-bg-elevated)", border: "1px solid var(--ep-border)", marginBottom: 32 }}>
          <IcCheck size={18}/>
          <div style={{ font: "400 15px/1.5 var(--ep-font-body)" }}>
            Welcome form submitted.
            {gate?.unlocked
              ? " Your transfer steps are unlocked."
              : " Transfer steps unlock as soon as your ICA signature completes."}
          </div>
        </div>
      ) : (
        <div style={{ marginBottom: 24 }}>
          <Sec>Your name</Sec>
          <div style={{ ...ROW, gridTemplateColumns: "1fr 1fr 1fr" }}>
            <div><Cap>First name *</Cap><input style={F} value={form.firstName} onChange={set("firstName")}/></div>
            <div><Cap>Middle name</Cap><input style={F} value={form.middleName} onChange={set("middleName")}/></div>
            <div><Cap>Last name *</Cap><input style={F} value={form.lastName} onChange={set("lastName")}/></div>
          </div>
          {emailPreview && (
            <div style={{ padding: "10px 14px", marginBottom: 16, background: "#FDF2E9", border: "1px solid var(--ep-sunset)",
              font: "400 13px/1.5 var(--ep-font-body)" }}>
              Your new team email will be created as <strong>{emailPreview}</strong>
            </div>
          )}
          <Cap>Name as it appears in ADRE</Cap><input style={F} value={form.nameInAdre} onChange={set("nameInAdre")}/>

          <Sec>Contact</Sec>
          <Cap>Street address *</Cap>
          <input style={F} value={form.address} onChange={set("address")} autoComplete="address-line1"/>
          <div style={ROW3}>
            <div>
              <Cap>City *</Cap>
              <input style={F} value={form.city} onChange={set("city")} autoComplete="address-level2"/>
            </div>
            <div>
              <Cap>State *</Cap>
              {/* Same list the licence section uses, but a separate answer: plenty
                  of agents hold an Arizona licence and live somewhere else. */}
              <select style={F} value={form.state} onChange={set("state")} autoComplete="address-level1">
                <option value="">Select…</option>{STATES.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <Cap>ZIP *</Cap>
              <input style={F} value={form.zip} onChange={set("zip")}
                inputMode="numeric" autoComplete="postal-code" placeholder="85018"/>
            </div>
          </div>
          <div style={ROW}>
            <div><Cap>Phone number *</Cap><input style={F} value={form.phone} onChange={set("phone")}/></div>
            <div><Cap>Email *</Cap><input style={F} value={form.email} onChange={set("email")}/></div>
          </div>
          <div style={ROW}>
            <div>
              <Cap>Birth month *</Cap>
              <select style={F} value={form.birthMonth} onChange={set("birthMonth")}>
                <option value="">Select…</option>{MONTHS.map(m => <option key={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <Cap>Birth day *</Cap>
              <select style={F} value={form.birthDay} onChange={set("birthDay")}>
                <option value="">Select…</option>{Array.from({length: 31}, (_, i) => <option key={i+1}>{i+1}</option>)}
              </select>
            </div>
          </div>

          <Sec>Licensing</Sec>
          <Cap>Real estate license number (e.g. SA987654321)</Cap>
          <input style={F} value={form.licenseNumber} onChange={set("licenseNumber")}/>
          <div style={ROW}>
            <div>
              <Cap>Do you have an MLS ID? *</Cap>
              <select style={F} value={form.hasMlsId} onChange={set("hasMlsId")}>
                <option value="">Select…</option><option value="yes">Yes</option><option value="no">No</option>
              </select>
            </div>
            {form.hasMlsId === "yes" && (
              <div><Cap>MLS ID *</Cap><input style={F} value={form.mlsId} onChange={set("mlsId")}/></div>
            )}
          </div>
          <div style={ROW}>
            <div>
              <Cap>License issued by state of</Cap>
              <select style={F} value={form.licenseState} onChange={set("licenseState")}>
                {STATES.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div><Cap>Date issued *</Cap><input type="date" style={F} value={form.licenseDateIssued} onChange={set("licenseDateIssued")}/></div>
          </div>

          <Sec>About you</Sec>
          <Cap>How did you hear about us? (agent who referred you) *</Cap>
          <input style={F} value={form.referredBy} onChange={set("referredBy")}/>
          <Cap>Other languages spoken</Cap>
          <input style={F} value={form.otherLanguages} onChange={set("otherLanguages")}/>
          <Cap>Instagram handle (so we can tag you in team marketing)</Cap>
          <input style={F} value={form.instagramHandle} onChange={set("instagramHandle")} placeholder="@yourhandle"/>

          <Cap>Getting to know you — question #1 *</Cap>
          <select style={F} value={form.gtky1Question} onChange={set("gtky1Question")}>
            <option value="">Select a question…</option>
            {GTKY_OPTIONS.map(o => <option key={o}>{o}</option>)}
          </select>
          <Cap>Your answer *</Cap>
          <input style={F} value={form.gtky1Answer} onChange={set("gtky1Answer")}/>
          <Cap>Getting to know you — question #2 *</Cap>
          <select style={F} value={form.gtky2Question} onChange={set("gtky2Question")}>
            <option value="">Select a question…</option>
            {GTKY_OPTIONS.filter(o => o !== form.gtky1Question).map(o => <option key={o}>{o}</option>)}
          </select>
          <Cap>Your answer *</Cap>
          <input style={F} value={form.gtky2Answer} onChange={set("gtky2Answer")}/>

          <Sec>Your business</Sec>
          <Cap>Are you bringing any pending deals or listings? *</Cap>
          <select style={F} value={form.bringingPendingDeals} onChange={set("bringingPendingDeals")}>
            <option value="">Select…</option><option value="yes">Yes</option><option value="no">No</option>
          </select>

          {error && <div style={{ color: "var(--ep-sunset)", marginBottom: 12, font: "400 14px/1.4 var(--ep-font-body)" }}>{error}</div>}
          {!canSubmit && <div style={{ marginTop: 4, font: "400 12px/1.4 var(--ep-font-body)", color: "var(--ep-sunset)" }}>
            Still needed: {missing.join(", ")}
          </div>}
        </div>
      )}

      <FooterNav onBack={onBack}
        onNext={async () => {
          if (done) return onNext();                    // submitted + gate open → advance
          if (!canSubmit || state === "saving") return; // incomplete → no-op (hint shows what's missing)
          await submit();                               // footer button IS the submit
        }}
        canContinue={done ? !!gate?.unlocked : (canSubmit && state !== "saving")}
        nextLabel={
          done
            ? (gate?.unlocked ? "Continue" : "Awaiting your ICA signature…")
            : state === "saving" ? "Saving…" : "Submit the form to continue"
        }/>
    </div>
  );
}

export { StepWelcomeForm };
